import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const { isAuthenticated, isAdmin, user } = useAuth();

  return (
    <div className="min-h-screen bg-ink-50 text-ink-900 font-body">
      <nav
        className="print:hidden sticky top-0 z-50 border-b border-ink-200 bg-ink-50/90 backdrop-blur-md"
        data-testid="site-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group min-w-0"
            data-testid="nav-home-link"
          >
            <img
              src="/brand-ad-mark.png"
              alt="Akshat Dani"
              className="h-9 w-auto object-contain shrink-0"
            />
            <span className="hairline w-6 hidden sm:block shrink-0" />
            <span className="font-heading text-[15px] font-medium tracking-tight text-ink-900 whitespace-nowrap hidden sm:inline">
              Framework Cheat Sheet Hub
            </span>
            <span className="font-heading text-sm font-medium tracking-tight text-ink-900 whitespace-nowrap sm:hidden">
              FCH
            </span>
          </Link>
          <div className="flex items-center gap-5">
            {!isHome && (
              <Link
                to="/"
                className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-500 hover:text-ink-900 transition-colors hidden sm:inline"
                data-testid="nav-back-home"
              >
                Industries
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/account"
                data-testid="nav-bookmarks"
                className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-500 hover:text-vermilion transition-colors hidden sm:inline"
              >
                Bookmarks
              </Link>
            )}
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="font-mono text-[11px] uppercase tracking-wide-2 text-vermilion hover:text-vermilion-deep transition-colors hidden sm:inline"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  data-testid="nav-account"
                  className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-900 hover:text-ink-700 transition-colors max-w-[160px] truncate"
                  title={user?.email}
                >
                  {user?.email}
                </Link>
              </>
            ) : (
              <Link
                to="/signin"
                data-testid="nav-signin"
                className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-900 hover:text-ink-700 transition-colors"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </nav>

      <main>{children}</main>

      <footer className="print:hidden border-t border-ink-200 mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              The library
            </p>
            <p className="mt-4 font-heading text-2xl text-ink-900 leading-snug">
              A reference of sixty strategic frameworks, written for the working consultant.
            </p>
          </div>
          <div className="md:col-span-3 md:col-start-7">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-4">
              Methodology
            </p>
            <ul className="space-y-2 text-sm text-ink-600 leading-relaxed">
              <li>60 canonical frameworks</li>
              <li>1,500 documented problems</li>
              <li>AI-curated recommendations</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-4">
              Access
            </p>
            <ul className="space-y-2 text-sm text-ink-600 leading-relaxed">
              <li>
                {isAuthenticated ? (
                  <Link to="/account" className="hover:text-ink-900">My account</Link>
                ) : (
                  <Link to="/signup" className="hover:text-ink-900">Open an account</Link>
                )}
              </li>
              <li>Unlimited access for members</li>
              <li>Bookmark + citation export</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-ink-200">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              © {new Date().getFullYear()} Framework Cheat Sheet Hub · All rights reserved
            </p>
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              Built for senior consultants and strategy practitioners
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
